import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Actions, ofType } from '@ngrx/effects';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { SearchTagsAndCategoriesGQL, TagStatus } from 'generated/graphql';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { debounceAllButFirst } from 'helpers';

import {
  FIELD_RESPONSE_QUICK_SEARCH_CLEAR_SEARCH_TEXT
} from './field-response-quicksearch.actions';

interface SearchResultDTO {
  type: 'Tag' | 'TagCategory';
  groupName: string;
  categoryId: string;
  categoryName: string;
  tagName?: string;
  tagId?: string;
}

export interface FieldResponseQSSelectedEvent {
  categoryId: string;
  tagId: string;
  tagName: string;
}

@Component({
  selector: 'app-field-response-quicksearch',
  templateUrl: `./field-response-quicksearch.component.html`
})
export class FieldResponseQuickSearchComponent extends WithDisposableSubject()
  implements OnInit {

  @Input()
  targetType: 'EntityType' | 'EntityConnectionType';

  @Input()
  targetTypeId: string;

  @Input()
  archivedOnly: boolean;

  @Output() resultSelected = new EventEmitter<FieldResponseQSSelectedEvent>();

  searchInput = new UntypedFormControl();

  searchResults$: Observable<any[]>;

  loading = false;
  /**
   *
   */
  constructor(
    private _searchTagsAndCategories: SearchTagsAndCategoriesGQL,
    private actions$: Actions
  ) {
    super();
  }

  ngOnInit() {
    this.actions$.pipe(
      ofType(FIELD_RESPONSE_QUICK_SEARCH_CLEAR_SEARCH_TEXT),
      takeUntil(this.disposer)
    ).subscribe(() => {
      this.searchInput.setValue('');
    });

    this.searchResults$ = this.searchInput.valueChanges.pipe(
      filter(sr => typeof sr === 'string'),
      debounceAllButFirst(250),
      tap(() => this.loading = true),
      switchMap(keyword => this._searchTagsAndCategories.watch({
        keyword,
        targetType: this.targetType,
        targetTypeId: this.targetTypeId,
        status: this.archivedOnly ? TagStatus.Archived : TagStatus.Active
      }, {
        fetchPolicy: 'cache-and-network'
      }).valueChanges.pipe(
        catchError(er => EMPTY)
      ))
    ).pipe(
      filter(res => !!res.data),
      map(res => res.data),
      map(data => {
        const categories = data.categories.nodes
          .filter(
            (cat) => this.archivedOnly ? cat.hasArchivedTags : (!cat.hasAssociatedTags || cat.hasActiveTags)
          )
          .map(c => this.categoryToResultDTO(c));

        const tags = data.tags.nodes
          .filter(t => this.archivedOnly ? t.archived : !t.archived)
          .map(t => this.tagToResultDTO(t));

        return [...categories, ...tags];
      }),
      tap(() => this.loading = false)
    );
  }

  displayWithFn(input: any) {
    return '';
  }

  tagToResultDTO(tag: { id: string; name: string; category: { id: string; name: string; groupName: string } }): SearchResultDTO {
    return {
      type: 'Tag',
      categoryId: tag.category.id,
      categoryName: tag.category.name,
      tagName: tag.name,
      tagId: tag.id,
      groupName: tag.category.groupName
    };
  }

  categoryToResultDTO(category: { id: string; name: string; groupName: string }): SearchResultDTO {
    return {
      type: 'TagCategory',
      categoryId: category.id,
      categoryName: category.name,
      groupName: category.groupName
    };
  }

  onResultSelected(event: MatAutocompleteSelectedEvent) {
    const result = event.option.value;

    this.resultSelected.emit({
      categoryId: result.categoryId,
      tagId: result.tagId,
      tagName: result.tagName
    });
  }
}
